import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
} from "semantic-ui-react";
import { useForm } from "react-hook-form";

import { AccordionTitleView, DropDown } from "../../../../../components";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { CULTIVATION_TYPE, HarvestSeasonData } from "../../../../../config/constants";
import { checkDateValidation, validateCultivationNumber } from "../../../../../utils/utils";

import "../styles/CultivarForm.scss";

const Fields = forwardRef((
  {
    currentDataType,
    currentFormData,
    currentOriginalFormData,
    onValidityChange = () => {}
  }: any,
  ref
) => {
  const [harvestSeason, setHarvestSeason] = useState<string>("Unknown")

  const values = useMemo(() => {
    setHarvestSeason(currentFormData?.cultivationField?.harvestSeason)
    return {
      yield: currentFormData?.cultivationField?.yield,
      harvestYear: currentFormData?.cultivationField?.harvestYear,
      growthArea: currentFormData?.cultivationField?.growthArea,
      harvestSeason: currentFormData?.cultivationField?.harvestSeason ?? "",
    }
  }, [currentFormData])

  const {
    formState: { errors },
    control,
    trigger,
    getValues
  } = useForm({
    defaultValues: values,
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: false
  });

  const hasErrors = Object.keys(errors).length > 0;

  useImperativeHandle(ref, () => ({
    getFieldData: async () => {
      const isValid = await trigger();

      if (!isValid) {
        return null;
      }

      let fieldData = getValues();
      fieldData.harvestSeason = harvestSeason
      return fieldData
    }
  }));

  useEffect(() => {
    onValidityChange(!hasErrors);
  }, [hasErrors, onValidityChange]);
  
  return (
    <div>
      <AccordionTitleView
        accordionEnabled={false}
        title={"Field"}
        plusIcon={false}
      />
      <div>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid columns={2}>
                <Grid.Row>
                  {currentOriginalFormData?.dataType === "Supplier" ? (
                    <Grid.Column style={{ marginBottom: 15 }}>
                      <p className="textLabel mb-0">Yield (kg/ha)</p>
                      <p className="textLabel">
                        {currentOriginalFormData?.cultivationField?.yield}
                      </p>
                    </Grid.Column>
                  ) : null}
                  <Grid.Column>
                    <p className="textLabel mb-0">Harvest year</p>
                    <p className="textLabel">
                      {currentOriginalFormData?.cultivationField?.harvestYear}
                    </p>
                  </Grid.Column>
                  {currentOriginalFormData?.dataType === "Supplier" ? (
                    <Grid.Column>
                      <p className="textLabel mb-0">Growth Area (ha)</p>
                      <p className="textLabel">
                        {currentOriginalFormData?.cultivationField?.growthArea}
                      </p>
                    </Grid.Column>
                  ) : null}
                  <Grid.Column>
                    <p className="textLabel mb-0">Harvest season</p>
                    <p className="textLabel">
                      {currentOriginalFormData?.cultivationField?.harvestSeason}
                    </p>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid>
                {currentDataType === CULTIVATION_TYPE.Supplier ? (
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0
                            ? ""
                            : value.toString(),
                        output: (e: any) => {
                          return e;
                        },
                      }}
                      onChange={(e) => {
                        trigger("yield");
                      }}
                      control={control}
                      labelName={"Yield (kg/ha)"}
                      placeholder="Yield (kg/ha)"
                      name="yield"
                      errors={errors.yield}
                      otherValidation={{
                        validate: {
                          valueCheck: (v: any) => validateCultivationNumber(v)
                        }
                      }}
                    />
                  </Grid.Column>
                ) : null}
                <Grid.Column computer={8} tablet={8} mobile={16}>
                  <InputNumber
                    labelName={"Harvest year"}
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return checkDateValidation(e.target.value);
                      },
                    }}
                    control={control}
                    errors={errors.harvestYear}
                    errorMessage={"Please insert valid year"}
                    name="harvestYear"
                    isYearInput={true}
                    customInputValidation={true}
                  />
                </Grid.Column>
                {currentDataType === CULTIVATION_TYPE.Supplier ? (
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0
                            ? ""
                            : value.toString(),
                        output: (e: any) => {
                          return e;
                        },
                      }}
                      control={control}
                      labelName={"Growth Area (ha)"}
                      placeholder="Growth Area (ha)"
                      name="growthArea"
                      errors={errors.growthArea}
                      otherValidation={{
                        validate: {
                          valueCheck: (v: any) => validateCultivationNumber(v)
                        }
                      }}
                    />
                  </Grid.Column>
                ) : null}
                <Grid.Column computer={8} tablet={8} mobile={16}>
                  <DropDown
                    labelName={"Harvest season"}
                    placeholder="Select Season"
                    currentData={HarvestSeasonData}
                    defaultValue={harvestSeason}
                    handleChangeState={(e: any, { value }: any) => {
                      setHarvestSeason(value);
                    }}
                  />
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
})

export default Fields;
